import {
  userSlice,
  useGetApiV1UsersByUserIdFollowersQuery,
  useGetApiV1UsersRemoteByUserNameFullFollowersQuery,
  isHiddenListError,
} from '@wellesley/user'

import { useSelector } from 'react-redux'
import { authSlice } from '@wellesley/auth'
import { UserFull } from '@wellesley/openapi'
import { useEffect, useState } from 'react'

type HookProps = {
  skip?: boolean
  user?: UserFull
  limit?: number
}

const DEFAULT_LIMIT = 20

/**
 * Simple hook for fetching followers (first page only).
 * Use useFollowersInfinite for paginated/infinite scroll.
 */
const useFollowers = ({
  user,
  skip = false,
  limit = DEFAULT_LIMIT,
}: HookProps) => {
  const myUser = useSelector(userSlice.selectors.selectCurrentUser)
  const isLogged = useSelector(authSlice.selectors.selectIsAuthenticated)
  const canFetch = !skip && isLogged

  const [isHidden, setIsHidden] = useState(false)

  // Reset isHidden when user changes
  useEffect(() => {
    setIsHidden(false)
  }, [user?.id])

  const remoteUserName =
    user && !user.local && user.domain
      ? `@${user.username}@${user.domain}`
      : undefined

  const isRemoteUser = !!remoteUserName
  const userIdToFetch = user?.id || myUser?.id

  const skipRemoteQuery = !isRemoteUser || isHidden || !canFetch
  const skipFollowersByIdQuery =
    isRemoteUser || !userIdToFetch || !canFetch || isHidden

  const {
    data: remoteUserFollowers,
    isLoading: isRemoteUserFollowersLoading,
    error: remoteFollowersError,
  } = useGetApiV1UsersRemoteByUserNameFullFollowersQuery(
    { userNameFull: remoteUserName!, offset: 0, limit },
    { skip: skipRemoteQuery },
  )

  const {
    data: followersByUserId,
    isLoading: isFollowersByIdLoading,
    error: followersByIdError,
  } = useGetApiV1UsersByUserIdFollowersQuery(
    { userId: userIdToFetch!, offset: 0, limit },
    { skip: skipFollowersByIdQuery },
  )

  const list = isRemoteUser ? remoteUserFollowers : followersByUserId

  const isLoading =
    (!skipRemoteQuery && isRemoteUserFollowersLoading) ||
    (!skipFollowersByIdQuery && isFollowersByIdLoading)

  useEffect(() => {
    if (
      isHiddenListError(followersByIdError) ||
      isHiddenListError(remoteFollowersError)
    ) {
      setIsHidden(true)
    }
  }, [followersByIdError, remoteFollowersError])

  return {
    followers: list,
    isLoading,
    isHidden,
  }
}

export default useFollowers
